/**
 * NewQuestNotification - Unlocked quest accept card
 *
 * Appears when a non-chain quest becomes eligible (via unlock conditions).
 * Shows quest details with Accept/Dismiss buttons so players can accept
 * directly from the QuestDock without navigating to the quest board.
 */

import { useMemo } from 'react';
import { QuestDefinition, QuestRewardType } from '@/types';

interface NewQuestNotificationProps {
  questId: string;
  questName: string;
  definition?: QuestDefinition;
  onAccept?: (questId: string) => void;
  onNavigate: () => void;
  onDismiss: () => void;
}

export function NewQuestNotification({
  questId,
  questName,
  definition,
  onAccept,
  onNavigate,
  onDismiss,
}: NewQuestNotificationProps) {
  const rewardSummary = useMemo(() => {
    if (!definition) return '';
    return definition.rewards
      .map((r) => {
        switch (r.type) {
          case QuestRewardType.CURRENCY:
            return `+${r.value} gold`;
          case QuestRewardType.INVENTORY_EXPANSION:
            return `+${r.value} inventory`;
          case QuestRewardType.EXPERIENCE:
            return `+${r.value} XP`;
          default:
            return r.description || '';
        }
      })
      .filter(Boolean)
      .join(', ');
  }, [definition]);

  // Full card with quest details when definition is available
  if (definition) {
    return (
      <div className="bg-gradient-to-b from-blue-900/30 to-blue-900/20 border-2 border-blue-500/60 rounded-lg p-4 transition-all">
        {/* Header */}
        <div className="flex items-center gap-2 mb-3">
          <span className="text-2xl">🔔</span>
          <h4 className="text-sm font-bold text-blue-300">New Quest Available!</h4>
        </div>

        {/* Quest details */}
        <div className="bg-gray-800/60 rounded-lg p-3 mb-3">
          <h5 className="text-sm font-bold text-white mb-1">
            {definition.isTutorial && <span className="text-yellow-400">⭐ </span>}
            {definition.name}
          </h5>
          <p className="text-xs text-gray-400 mb-2">{definition.description}</p>
          {rewardSummary && (
            <p className="text-xs text-yellow-400/80">Rewards: {rewardSummary}</p>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => onAccept?.(questId)}
            className="flex-1 bg-green-600 hover:bg-green-500 text-white px-3 py-2 rounded-lg text-sm font-bold transition-colors cursor-pointer"
          >
            Accept Quest
          </button>
          <button
            onClick={onDismiss}
            className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-sm transition-colors cursor-pointer"
          >
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  // Fallback: compact notification when definition isn't available
  return (
    <div className="bg-blue-900/30 border border-blue-500/50 rounded-lg px-3 py-2 flex items-center gap-2">
      <span className="text-lg shrink-0">🔔</span>
      <p className="text-xs text-blue-200 flex-1 min-w-0">
        New quest unlocked: <span className="font-bold text-white">&quot;{questName}&quot;</span>
      </p>
      <div className="flex gap-1.5 shrink-0">
        <button
          onClick={onNavigate}
          className="text-xs bg-blue-600 hover:bg-blue-500 text-white px-2 py-1 rounded transition-colors cursor-pointer whitespace-nowrap"
        >
          Quest Board →
        </button>
        <button
          onClick={onDismiss}
          className="text-xs text-gray-400 hover:text-white px-1 py-1 transition-colors cursor-pointer"
          aria-label="Dismiss notification"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
